/**
 * Shared scheduled-reply state for the Candidate Requests tab.
 *
 * Same shape as `useReminders`: a module-level store with subscribers, because the schedule button
 * lives deep inside `InboundCard` and the strip at the top of `RequestsTab`, and both host files sit
 * at the 500-line ceiling. In memory only — never localStorage, since a row names a candidate.
 *
 * ⚠️ **The queue is the SERVER's, not the client's.** `RequestsTab.applyResponse` replaces `items`
 * wholesale on every refetch, so a "this one is scheduled" flag kept on the item would vanish the
 * next time the inbox is checked. That is why the scheduled state comes from its own endpoint and is
 * joined onto the items by the MESSAGE id at render time (see `useDecoratedRequests`): the join is
 * recomputed from server state, so no refetch can lose it.
 *
 * **It also has to keep asking.** The worker moves rows to `sent`, `failed` or `superseded` on its
 * own schedule, with nothing to tell the browser — so a store loaded once would suppress the send
 * button forever for a reply that has already gone out. Hence `REFRESH_MS` below.
 *
 * Cancelling is optimistic with rollback, like the reminder mutations. Scheduling is NOT: a queued
 * reply is a promise to mail a candidate, so the row appears only once the server has it.
 *
 * PII: analytics carry booleans, codes and lead-times ONLY — never the reply, the candidate name or
 * the thread id (the same rule the feedback widget follows).
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taScheduledSendsApi';
import type { ScheduledSend } from '../../services/taScheduledSendsApi';

interface ScheduledState {
  items: ScheduledSend[];
  status: 'idle' | 'loading' | 'ready' | 'failed';
}

let state: ScheduledState = { items: [], status: 'idle' };
const listeners = new Set<() => void>();

/**
 * Bumped by every reset. Async work captures it and throws its result away if it changed meanwhile:
 * without that, a `load` still in flight when someone signs out would repopulate this module-level
 * store with the PREVIOUS recruiter's candidates, after the reset had cleared them.
 */
let generation = 0;

/**
 * Bumped by every LOCAL mutation (schedule, cancel, dismiss). A list response is only committed when
 * this has not moved since the request went out.
 *
 * Necessary once the store refreshes on a timer: an interval `load` can already be in flight when
 * the recruiter schedules or cancels something, and its older list would then drop the new row or
 * bring the cancelled one back. The local mutation is the newer truth in both cases; the next tick
 * reconciles with the server.
 */
let mutations = 0;

/** Monotonic load id, so an older list can never overwrite a newer one that already landed. */
let loadSeq = 0;
let committedLoad = 0;

/** How often a mounted tab re-asks. The worker ticks every five minutes, so this is the same order. */
const REFRESH_MS = 60_000;

function setState(next: ScheduledState) {
  state = next;
  listeners.forEach((l) => l());
}

const bySendAt = (items: ScheduledSend[]) =>
  [...items].sort((a, b) => a.sendAt.localeCompare(b.sendAt));

async function load() {
  const mine = generation;
  const seq = ++loadSeq;
  const mutationsAt = mutations;
  setState({ ...state, status: 'loading' });
  try {
    const items = bySendAt(await api.fetchScheduledSends());
    if (mine !== generation) return; // signed out (or reset) while this was in flight
    if (seq < committedLoad) return; // a newer list already landed
    if (mutations !== mutationsAt) return; // a local change is newer than this list
    committedLoad = seq;
    setState({ items, status: 'ready' });
  } catch {
    if (mine !== generation) return;
    /**
     * The strip renders nothing on failure. Deliberate, and worth stating: a failed load means the
     * tab does not KNOW about a queued reply, so the card shows its ordinary Send button again. The
     * send itself is still guarded server-side — `sendInboundReply` refuses a second answer to the
     * same question — so the worst case is a confusing button, not two mails.
     */
    setState({ ...state, status: 'failed' });
  }
}

/**
 * Empty the store and tell every subscriber. Two callers: tests between cases, and AuthContext on
 * sign-out — rows name candidates, so a shared browser must not carry them to the next account, and
 * a leftover 'ready' status would skip that account's load.
 */
export function resetScheduledSendsStore() {
  generation += 1;
  committedLoad = 0;
  setState({ items: [], status: 'idle' });
}

const leadMinutes = (sendAt: string) => Math.round((Date.parse(sendAt) - Date.now()) / 60_000);

export function useScheduledSends() {
  const snapshot = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state
  );

  // First consumer loads; a FAILED load retries on the next mount, so one blip does not hide the
  // queue for the whole session (the lesson from useReminders).
  useEffect(() => {
    if (state.status === 'idle' || state.status === 'failed') void load();
  }, []);

  /**
   * Keep asking while anything is mounted. The worker sends and fails on its own, and nothing pushes
   * that to the browser — without this the card would keep showing "Scheduled for …" long after the
   * reply had gone out, and its send button would stay hidden.
   */
  useEffect(() => {
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  /**
   * Pending rows keyed by the MESSAGE they answer, not by thread.
   *
   * A candidate can ask a second question on the same thread after the first reply is queued. Keyed
   * by thread, that new question would be decorated as scheduled and lose its send button, while the
   * queued row answers the earlier message — the recruiter would be unable to reply to a live
   * question. `sending` counts as pending here: not cancellable, but not done either.
   */
  const pendingByMessage = useMemo(() => {
    const map = new Map<string, ScheduledSend>();
    for (const row of snapshot.items) {
      // Only replies answer a message and decorate a request card; a rejection has no
      // targetMessageId and belongs to no card here.
      if ((row.status === 'pending' || row.status === 'sending') && row.targetMessageId)
        map.set(row.targetMessageId, row);
    }
    return map;
  }, [snapshot.items]);

  const schedule = useCallback(
    async (input: {
      threadId: string;
      opportunityId?: string | null;
      sendAt: string;
      body: string;
      gmailToken: string;
    }) => {
      const mine = generation;
      // Not optimistic: this is a promise to mail a candidate, so it exists once the server says so.
      const row = await api.scheduleReply(input); // caller shows validation / grant errors
      if (mine !== generation) return row; // reset meanwhile: never repopulate the cleared store
      mutations += 1; // an in-flight list must not drop this row
      setState({
        ...state,
        items: bySendAt([...state.items.filter((r) => r.id !== row.id), row]),
      });
      void logAnalyticsEvent('ta_reply_scheduled', { leadMinutes: leadMinutes(row.sendAt) });
      return row;
    },
    []
  );

  /**
   * Change when a queued reply goes out.
   *
   * Not optimistic, unlike `cancel`: the server is the one that decides whether the new time is
   * allowed at all (too soon, too far ahead, or the worker already holds the row), so showing a new
   * time before it has agreed would sometimes have to be taken back.
   */
  const reschedule = useCallback(async (row: ScheduledSend, sendAt: string) => {
    const mine = generation;
    const updated = await api.rescheduleSend(row.id, sendAt); // caller shows validation errors
    if (mine !== generation) return updated; // reset meanwhile: never repopulate a cleared store
    mutations += 1; // an in-flight list must not put the OLD time back
    setState({
      ...state,
      items: bySendAt(state.items.map((r) => (r.id === updated.id ? updated : r))),
    });
    void logAnalyticsEvent('ta_reply_schedule_moved', { leadMinutes: leadMinutes(updated.sendAt) });
    return updated;
  }, []);

  const cancel = useCallback(async (row: ScheduledSend) => {
    const mine = generation;
    mutations += 1; // an in-flight list must not bring this row back
    // Optimistic, with the reminder store's per-row rollback: restoring a whole snapshot would
    // resurrect rows a concurrent operation had meanwhile removed successfully.
    setState({ ...state, items: state.items.filter((r) => r.id !== row.id) });
    try {
      await api.cancelScheduledSend(row.id);
      void logAnalyticsEvent('ta_reply_schedule_cancelled', {});
    } catch (err) {
      // A rollback into a store that has since been reset would resurrect a foreign row.
      if (mine !== generation) return;
      mutations += 1; // the rollback is itself newer than any list already in flight
      if (!state.items.some((r) => r.id === row.id)) {
        setState({ ...state, items: bySendAt([...state.items, row]) });
      }
      const inFlight = (err as Error)?.name === 'SendInFlight';
      // The strip now carries rejections as well as replies (Feedback tab); call the row what it is
      // so a failed rejection cancel does not report itself as a reply.
      const noun = row.kind === 'rejection' ? 'rejection' : 'reply';
      showToast({
        status: inFlight ? 'warning' : 'error',
        description: inFlight
          ? `This ${noun} is going out right now — too late to stop it.`
          : `Could not cancel the scheduled ${noun} — it is back.`,
      });
      if (inFlight) void load(); // the row's real state is now the server's
    }
  }, []);

  /** Clear a finished row (sent / cancelled / superseded / failed) from the strip, locally only. */
  const dismiss = useCallback((row: ScheduledSend) => {
    mutations += 1; // otherwise the next list would put the dismissed row straight back
    setState({ ...state, items: state.items.filter((r) => r.id !== row.id) });
  }, []);

  return {
    scheduled: snapshot.items,
    pendingByMessage,
    loading: snapshot.status === 'loading' || snapshot.status === 'idle',
    failed: snapshot.status === 'failed',
    reload: load,
    schedule,
    reschedule,
    cancel,
    dismiss,
  };
}
